import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { TypeGetOrderSearchConditionsResponse } from '~/types/delivery-agency.types';

/**
 * [배송 주문 관리] 검색 필터 코드 조회 API
 */
export const fetchOrdersSearchConditions = async (): Promise<TypeGetOrderSearchConditionsResponse['data']> => {
  const { data } = await axiosInstance.get<TypeGetOrderSearchConditionsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_ORDERS_CODES
  );
  return data.data;
};

/**
 * [배송 주문 관리] 검색 필터 코드 조회 Query Hook
 */
export const useGetOrdersSearchConditionsQuery = (options?: { enabled?: boolean; showGlobalErrorToast?: boolean }) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.orderSearchConditions(),
    queryFn: fetchOrdersSearchConditions,
    staleTime: 1000 * 60 * 30,
    enabled: options?.enabled ?? true,
    meta: { showGlobalErrorToast: options?.showGlobalErrorToast ?? true },
  });
};

/**
 * [배송 주문 관리] 검색 필터 코드 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<TypeGetOrderSearchConditionsResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/orders/search-conditions',
  label: '배송 주문 관리/검색 필터 코드',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          searchKeywordTypes: [
            { code: 'OUTBOUND_CODE', description: '출고번호' },
            { code: 'CLIENT_ORDER_CODE', description: '판매처 주문번호' },
            { code: 'RECEIVER_NAME', description: '수령자명' },
            { code: 'ORDERER_NAME', description: '주문자명' },
            { code: 'PRODUCT_NAME', description: '상품명' },
          ],
          orderStatuses: [
            { code: 'OUTBOUND_READY', description: '출고 대기' },
            { code: 'OUTBOUND_PROCESSING', description: '출고 진행중' },
            { code: 'ON_THE_WAY', description: '배송 중' },
            { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
            { code: 'CANCELED', description: '주문 취소' },
            { code: 'DELIVERY_FAILED', description: '배송 실패' },
          ],
          deliveryTypes: [
            { code: 'DAWN', description: '샛별배송' },
            { code: 'DAY', description: '하루배송' },
          ],
          courierCodes: [
            { code: 'NM', description: '넥스트마일' },
            { code: 'CJT', description: 'CJ' },
            { code: 'LTT', description: '롯데' },
          ],
          sortOptions: [
            { code: 'CREATED_AT_DESC', description: '등록일 (내림차순)' },
            { code: 'CREATED_AT_ASC', description: '등록일 (오름차순)' },
          ],
        },
      },
    },
  ],
});
